import { HttpClient } from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';
import { StaticPagesHelper } from '@features/static-pages/helpers/static-pages-helper';
import { ENV, PRISMIC_CONFIG } from '@rd/core/tokens/rd-lib.token';
import {
  AlertContent,
  AlertsApiResponse,
  CampaignBanner,
  CampaignBannerApiResponse,
  CampaignBannersApiResponse,
  CampaignPageApiResponse,
  CampaignPageBanner,
  CampaignPageBannerApiResponse,
  CampaignPageContentBlock,
  CampaignPageContentBlockApiResponse,
  DocumentType,
  FeaturedPartnerType,
  FullPage,
  FullPageType,
  Locale,
  MultipleCampaignBanner,
  MultipleFeaturedPartners,
  MultipleFeaturedPartnersV2,
  PageContent,
  PageContentApiResponse,
  PrismicConfig,
  PrismicFeaturedPartnerApiResponse,
  PrismicFeaturedPartnerV2ApiResponse,
  PrismicRefApiResponse,
  TermsApiResponse,
  TermsContent,
} from '@rd/types';
import * as PrismicDOM from 'prismic-dom';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CmsApiService {
  constructor(
    private httpClient: HttpClient,
    private staticPagesHelper: StaticPagesHelper,
    @Inject(PRISMIC_CONFIG) private prismicConfig: PrismicConfig,
  ) {}

  private env = inject(ENV);

  getPageContent(
    pageName: string,
    localeCode: Locale['code'],
    ref: string,
  ): Observable<PageContent | null> {
    const fullPageName = this.staticPagesHelper.fullPageName(pageName);
    const constructedUrl = this.buildUrlParams(
      fullPageName,
      localeCode,
      'pagecontent',
      ref,
    );
    return this.httpClient
      .get<PageContentApiResponse>(encodeURI(constructedUrl))
      .pipe(map((response) => this.transformPageContentResponse(response)));
  }

  getTermsContent(
    pageName: string,
    localeCode: Locale['code'],
    ref: string,
  ): Observable<TermsContent | null> {
    const constructedUrl = this.buildUrlParams(
      pageName,
      localeCode,
      'terms',
      ref,
    );
    return this.httpClient
      .get<TermsApiResponse>(encodeURI(constructedUrl))
      .pipe(map((response) => this.transformTermsResponse(response)));
  }

  getFeaturedPartners(
    pageName: string,
    localeCode: Locale['code'],
    ref: string,
  ): Observable<MultipleFeaturedPartners | null> {
    const fullPageName = this.staticPagesHelper.fullPageName(pageName);
    const constructedUrl = this.buildUrlParams(
      fullPageName,
      localeCode,
      'featuredpartners',
      ref,
    );
    return this.httpClient
      .get<PrismicFeaturedPartnerApiResponse>(encodeURI(constructedUrl))
      .pipe(
        map((response) => this.transformFeaturedPartnersResponse(response)),
      );
  }

  getFeaturedPartnersV2(
    pageName: string,
    localeCode: Locale['code'],
    ref: string,
  ): Observable<MultipleFeaturedPartnersV2 | null> {
    const fullPageName = this.staticPagesHelper.fullPageName(pageName);
    const constructedUrl = this.buildUrlParams(
      fullPageName,
      localeCode,
      'feature_partner_v2',
      ref,
    );
    return this.httpClient
      .get<PrismicFeaturedPartnerV2ApiResponse>(encodeURI(constructedUrl))
      .pipe(
        map((response) => this.transformFeaturedPartnersResponseV2(response)),
      );
  }

  getCampaignBanner(
    id: string,
    localeCode: Locale['code'],
    ref: string,
  ): Observable<CampaignBanner | null> {
    const constructedUrl = this.buildUrlParams(
      id,
      localeCode,
      'campaignbanner',
      ref,
    );
    return this.httpClient
      .get<CampaignBannerApiResponse>(encodeURI(constructedUrl))
      .pipe(map((response) => this.transformCampaignBanner(response)));
  }

  getCampaignBanners(
    id: string,
    localeCode: Locale['code'],
    ref: string,
  ): Observable<MultipleCampaignBanner | null> {
    const fullPageName = this.staticPagesHelper.fullPageName(id);
    const constructedUrl = this.buildUrlParams(
      fullPageName,
      localeCode,
      'campaignbannermultiple',
      ref,
    );
    return this.httpClient
      .get<CampaignBannersApiResponse>(encodeURI(constructedUrl))
      .pipe(map((response) => this.transformCampaignBanners(response)));
  }

  getFullPage(
    id: string,
    localeCode: Locale['code'],
    variant: FullPageType,
    ref: string,
  ): Observable<FullPage | null> {
    const constructedUrl = this.buildUrlParams(id, localeCode, variant, ref);
    return this.httpClient
      .get<CampaignPageApiResponse>(encodeURI(constructedUrl))
      .pipe(map((response) => this.transformFullPage(response, variant)));
  }

  getAlerts(
    pageName: string,
    localeCode: Locale['code'],
    ref: string,
  ): Observable<AlertContent | null> {
    const fullPageName = this.staticPagesHelper.fullPageName(pageName);
    const constructedUrl = this.buildUrlParams(
      fullPageName,
      localeCode,
      'alerts',
      ref,
    );

    return this.httpClient
      .get<AlertsApiResponse>(encodeURI(constructedUrl))
      .pipe(map((response) => this.transformAlerts(response)));
  }

  buildUrlParams<CmsDocumentType = DocumentType>(
    pageName: string,
    localeCode: Locale['code'],
    type: CmsDocumentType,
    ref: string,
  ): string {
    const { apiUrl, accessToken } = this.prismicConfig;

    const baseUrl = `${apiUrl}/documents/search`;
    const urlParams = {
      ref,
      access_token: accessToken,
      q: `[[at(my.${type}.uid, "${pageName}")]]`,
      format: 'json',
      lang: localeCode.toLowerCase(),
    };

    return `${baseUrl}?${this.convertToQueryString(urlParams)}`;
  }

  getApiRef(): Observable<string | null> {
    const { apiUrl } = this.prismicConfig;
    return this.httpClient.get<PrismicRefApiResponse>(`${apiUrl}`).pipe(
      map((response: PrismicRefApiResponse) => {
        const refItem = response.refs.find((res) => res.isMasterRef);
        return refItem ? refItem.ref : null;
      }),
    );
  }

  private transformFeaturedPartnersResponse(
    response: PrismicFeaturedPartnerApiResponse,
  ): MultipleFeaturedPartners | null {
    if (response?.results[0]) {
      const { items } = response.results[0].data.body[0];
      return {
        type: 'featuredpartners',
        items: items.map((item) => ({
          description: item.description[0].text,
          image: item.image.url,
          title: item.title[0].text,
          partnerQueryString: item.partner_query_string[0].text,
          ariaLabel: item.aria_label[0].text,
          type: item.type[0].text,
          ctaText: item.cta_text[0]?.text || null,
          ctaUrl: item.cta_url.link_type === 'Web' ? item.cta_url.url : null,
          showFeaturedPartner: item.show_featured_partner,
          previewFeaturedPartner: item.preview_featured_partner,
        })),
      };
    }
    return null;
  }

  private transformFeaturedPartnersResponseV2(
    response: PrismicFeaturedPartnerV2ApiResponse,
  ): MultipleFeaturedPartnersV2 | null {
    const body = response?.results[0]?.data?.body[0];
    if (!body?.items) {
      return null;
    }

    const headingTitle = response.results[0].data.heading_title;
    return {
      type: 'feature_partner_v2',
      heading: headingTitle?.[0]?.text,
      items: body.items.map((item) => ({
        description: item.description?.[0]?.text ?? '',
        desktopImage: item.desktop_image?.url ?? '',
        mobileImage: item.mobile_image?.url ?? '',
        title: item.title?.[0]?.text ?? '',
        partnerQueryString: item.partner_query_string?.[0]?.text ?? '',
        ariaLabel: item.aria_label?.[0]?.text ?? '',
        type: item.type?.[0]?.text ?? FeaturedPartnerType.giftCard,
        ctaText: item.cta_text?.[0]?.text ?? null,
        ctaUrl: item.cta_url.link_type === 'Web' ? item.cta_url.url : null,
        showFeaturedPartner: item.show_featured_partner,
        previewFeaturedPartner: item.preview_featured_partner,
      })),
    };
  }

  private transformPageContentResponse(
    response: PageContentApiResponse,
  ): PageContent | null {
    if (response?.results[0]) {
      const result = response.results[0];
      const { title, pagesections } = result.data;

      return {
        type: 'pagecontent',
        title: title[0].text,
        name: result.uid,
        pageSections: pagesections.map((pageSection) => {
          return {
            title: pageSection.title1[0]?.text,
            content: PrismicDOM.RichText.asText(pageSection.content),
            boxed: pageSection.boxed,
          };
        }),
      };
    }

    return null;
  }

  private transformTermsResponse(
    response: TermsApiResponse,
  ): TermsContent | null {
    if (response?.results[0]) {
      const result = response.results[0];
      const { content } = result.data;

      return {
        type: 'terms',
        name: result.uid,
        content: content.map(({ text }) => text).join(''),
      };
    }

    return null;
  }

  private transformCampaignBanner(
    response: CampaignBannerApiResponse,
  ): CampaignBanner | null {
    if (response?.results[0]) {
      const result = response.results[0];
      const {
        banner_main_content,
        banner_sub_content,
        banner_title,
        banner_link,
        desktop_banner_image,
        mobile_banner_image,
        show_campaign_banner,
        preview_campaign_banner,
        sku_list,
      } = result.data;
      return {
        type: 'campaignbanner',
        name: result.uid,
        mainContent: banner_main_content.map(({ text }) => text).join(''),
        subContent: banner_sub_content.map(({ text }) => text).join(''),
        title: banner_title.map(({ text }) => text).join(''),
        link: banner_link.map(({ text }) => text).join(''),
        desktopImageUrl: desktop_banner_image.url,
        mobileImageUrl: mobile_banner_image.url,
        showBanner: show_campaign_banner,
        previewBanner: preview_campaign_banner,
        skuList: new Set(
          sku_list
            .map(({ text }) => text)
            .join('\n')
            .split('\n')
            .map((sku) => sku.trim())
            .filter((sku) => Boolean(sku)),
        ),
      };
    }

    return null;
  }

  private transformCampaignBanners(
    response: CampaignBannersApiResponse,
  ): MultipleCampaignBanner | null {
    if (response?.results[0]) {
      const { items } = response.results[0].data.body[0];
      return {
        type: 'campaignbannermultiple',
        items: items
          .filter(
            ({ show_campaign_banner, preview_campaign_banner }) =>
              (this.env === 'production' && show_campaign_banner) ||
              (this.env !== 'production' && preview_campaign_banner),
          )
          .map(
            ({
              banner_main_content,
              banner_sub_content,
              banner_title,
              banner_link,
              desktop_banner_image,
              mobile_banner_image,
              show_campaign_banner,
              preview_campaign_banner,
            }) => ({
              mainContent: banner_main_content.map(({ text }) => text).join(''),
              subContent: banner_sub_content.map(({ text }) => text).join(''),
              title: banner_title.map(({ text }) => text).join(''),
              link: banner_link.map(({ text }) => text).join(''),
              desktopImageUrl: desktop_banner_image.url,
              mobileImageUrl: mobile_banner_image.url,
              showBanner: show_campaign_banner,
              previewBanner: preview_campaign_banner,
            }),
          ),
      };
    }
    return null;
  }

  private transformFullPage(
    response: CampaignPageApiResponse,
    pageVariant: FullPageType,
  ): FullPage | null {
    if (response?.results[0]) {
      const result = response.results[0];
      const {
        campaign_page_button,
        campaign_page_hero_image_description,
        campaign_page_hero_image_desktop,
        campaign_page_hero_image_mobile,
        campaign_page_main_description,
        campaign_page_main_description_heading,
        campaign_page_main_heading,
        campaign_page_promo_period,
        campaign_page_promo_period_heading,
        campaign_page_promo_period_image_desktop,
        campaign_page_promo_period_image_mobile,
        campaign_page_promo_period_icon,
        campaign_page_sub_heading,
        show_campaign_page,
        campaign_page_banner,
        campaign_page_banners_heading,
        campaign_page_content_block,
      } = result.data;
      return {
        type: pageVariant,
        name: result.uid,
        actionButton: campaign_page_button?.map(({ text }) => text).join(''),
        mainDescription: campaign_page_main_description
          ?.map(({ text }) => text)
          .join(''),
        mainDescriptionHeading: campaign_page_main_description_heading
          ?.map(({ text }) => text)
          .join(''),
        mainHeading: campaign_page_main_heading
          ?.map(({ text }) => text)
          .join(''),
        subHeading: campaign_page_sub_heading?.map(({ text }) => text).join(''),
        promoPeriod: campaign_page_promo_period
          ?.map(({ text }) => text)
          .join(''),
        promoPeriodHeading: campaign_page_promo_period_heading
          ?.map(({ text }) => text)
          .join(''),
        promoPeriodImageUrlDesktop:
          campaign_page_promo_period_image_desktop?.url,
        promoPeriodImageUrlMobile: campaign_page_promo_period_image_mobile?.url,
        promoPeriodIcon: campaign_page_promo_period_icon?.url,
        heroImageUrlDesktop: campaign_page_hero_image_desktop?.url,
        heroImageUrlMobile: campaign_page_hero_image_mobile?.url,
        heroImageDescription: campaign_page_hero_image_description
          ?.map(({ text }) => text)
          .join(''),
        campaignPageBannersHeading: campaign_page_banners_heading
          ?.map(({ text }) => text)
          .join(''),
        campaignPageBanners:
          this.transformCampaignPageBanner(campaign_page_banner),
        contentBlocks: this.transformCampaignPageContentBlock(
          campaign_page_content_block,
        ),
        showPage: show_campaign_page,
      };
    }

    return null;
  }

  private convertToQueryString(params: { [key: string]: string }): string {
    return Object.keys(params)
      .map((key) => key + '=' + params[key])
      .join('&');
  }

  private transformCampaignPageBanner(
    campaignPageBannersApiResponse: CampaignPageBannerApiResponse[],
  ): CampaignPageBanner[] | null {
    if (campaignPageBannersApiResponse) {
      const campaignPageBanners: CampaignPageBanner[] = [];
      campaignPageBannersApiResponse.forEach(
        (campaignPageBannerApiResponse) => {
          const campaignPageBanner: CampaignPageBanner = {
            campaignPageBannerActionLink:
              campaignPageBannerApiResponse.campaign_page_banner_action_link
                ?.map(({ text }) => text)
                .join(''),
            campaignPageBannerDescription:
              campaignPageBannerApiResponse.campaign_page_banner_description
                ?.map(({ text }) => text)
                .join(''),
            campaignPageBannerTitle:
              campaignPageBannerApiResponse.campaign_page_banner_title
                ?.map(({ text }) => text)
                .join(''),
            campaignPageBannerUrlDesktop:
              campaignPageBannerApiResponse.campaign_page_banner_image_desktop
                ?.url,
            campaignPageBannerUrlMobile:
              campaignPageBannerApiResponse.campaign_page_banner_image_mobile
                ?.url,
          };
          campaignPageBanners.push(campaignPageBanner);
        },
      );
      return campaignPageBanners;
    }

    return [];
  }

  private transformCampaignPageContentBlock(
    apiResponse: CampaignPageContentBlockApiResponse[],
  ): CampaignPageContentBlock[] {
    const response: CampaignPageContentBlock[] = [];
    apiResponse?.forEach((content) => {
      response.push({
        campaignPageBlockTitle: content.campaign_page_content_block_title
          ?.map(({ text }) => text)
          .join(''),
        campaignPageBlockBody: content.campaign_page_content_block_body
          ?.map(({ text }) => text)
          .join(''),
        campaignPageBlockImageUrlDesktop:
          content.campaign_page_banner_image_desktop?.url,
        campaignPageBlockImageUrlMobile:
          content.campaign_page_banner_image_mobile?.url,
      });
    });
    return response;
  }

  private transformAlerts(
    alertsApiResponse: AlertsApiResponse,
  ): AlertContent | null {
    if (alertsApiResponse?.results[0]) {
      const { items } = alertsApiResponse.results[0].data.body[0];
      return {
        type: 'alerts',
        items: items.map(
          ({ show_alert, preview_alert, alert_icon, alert_description }) => ({
            showAlert: show_alert,
            previewAlert: preview_alert,
            alertIcon: alert_icon.url,
            alertDescription: alert_description
              .map(({ text }) => text)
              .join(''),
          }),
        ),
      };
    }

    return null;
  }
}
